/**
 * Country Service
 * Fetches countries from static JSON file (primary) or backend API (fallback)
 * Matches mobile app approach for consistency
 */

import axiosInstance from "@/lib/axios-instance";
import countriesData from "@/lib/data/countries.json";

export interface Country {
  flags: {
    png: string;
    svg: string;
    alt?: string;
  };
  name: {
    common: string;
    official?: string;
  };
  /** ISO 3166-1 alpha-2 (e.g. "EG") — present when data includes it */
  cca2?: string;
  idd?: {
    root?: string;
    suffixes?: string[];
  };
}

let cachedCountries: Country[] | null = null;

/**
 * Load countries from static JSON file
 * This is the primary source, matching mobile app behavior
 */
function loadCountriesFromFile(): Country[] {
  try {
    // Import the JSON file (Next.js will handle this at build time)
    // The import is synchronous, so no need for async
    const countries = countriesData as Country[];
    return countries;
  } catch (error) {
    console.error("Error loading countries from static file:", error);
    throw error;
  }
}

/**
 * Fetch all countries from backend API (fallback)
 * Uses the same endpoint as mobile: GET /api/countries
 */
async function loadCountriesFromAPI(): Promise<Country[]> {
  try {
    const response = await axiosInstance.get<{
      status: string;
      code: number;
      message: string;
      data: Country[];
    }>("countries");

    // Handle response structure from backend
    const countries = response.data?.data || (Array.isArray(response.data) ? response.data : []);
    return countries;
  } catch (error) {
    console.error("Error fetching countries from API:", error);
    throw error;
  }
}

/**
 * Fetch all countries
 * Primary: Static JSON file (fast, reliable, offline support)
 * Fallback: Backend API (if file fails)
 */
export async function getCountries(forceRefresh: boolean = false): Promise<Country[]> {
  if (!forceRefresh && cachedCountries) {
    return cachedCountries;
  }

  try {
    // Try static file first (matches mobile app approach)
    const countries = loadCountriesFromFile();
    cachedCountries = countries;
    return countries;
  } catch (fileError) {
    console.warn("Failed to load countries from static file, trying API fallback...", fileError);
    
    try {
      // Fallback to backend API
      const countries = await loadCountriesFromAPI();
      cachedCountries = countries;
      return countries;
    } catch (apiError) {
      console.error("Both static file and API failed to load countries:", apiError);
      
      // Return cached data if available
      if (cachedCountries) {
        console.warn("Returning cached countries data");
        return cachedCountries;
      }
      
      throw new Error("Failed to load countries from both static file and API");
    }
  }
}

/**
 * ISO 3166-1 alpha-2 for city data lookup.
 * Static `countries.json` has no `cca2` field; flag CDN URLs embed the code (e.g. …/w320/eg.png → EG).
 */
export function getCountryIso2ForCities(country: Country | null | undefined): string | undefined {
  if (!country) return undefined;
  const fromField = country.cca2?.trim();
  if (fromField) return fromField.toUpperCase();

  const urls = [country.flags?.png, country.flags?.svg].filter(Boolean) as string[];
  for (const url of urls) {
    const m = url.match(/\/(?:w\d+\/)?([a-z]{2})\.(?:png|svg)/i);
    if (m?.[1]) {
      return m[1].toUpperCase();
    }
  }
  return undefined;
}

/**
 * Get country by name (case-insensitive)
 */
export function getCountryByName(countries: Country[], name: string): Country | undefined {
  return countries.find(
    (country) => country.name.common.toLowerCase() === name.toLowerCase()
  );
}

/**
 * Get user's country by IP geolocation
 * Uses the existing /api/country endpoint which returns country name
 */
export async function getUserCountryName(): Promise<string | null> {
  try {
    const response = await fetch("/api/country");
    if (!response.ok) throw new Error("Failed to fetch country");
    const data = await response.json();
    return data.country || null;
  } catch (error) {
    console.error("Error detecting user country:", error);
    return null;
  }
}

/**
 * Get user's country object by auto-detection
 */
export async function getUserCountry(): Promise<Country | null> {
  try {
    const countryName = await getUserCountryName();
    if (!countryName) return null;

    const countries = await getCountries();
    const country = getCountryByName(countries, countryName);
    return country || null;
  } catch (error) {
    console.error("Error getting user country:", error);
    return null;
  }
}

